import { debounce } from "lodash";
import { useCallback, useContext, useEffect } from "react";
import {
    useRecoilState,
    useRecoilValue,
    useResetRecoilState,
    useSetRecoilState,
} from "recoil";
import io from "socket.io-client";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
    actionState,
    askUserState,
    audioConnectionState,
    callFnState,
    chatProfileState,
    chatSettingsInputsState,
    chatSettingsValueState,
    commandsState,
    currentThreadIdState,
    elementState,
    firstUserInteraction,
    isAiSpeakingState,
    loadingState,
    mcpState,
    messagesState,
    resumeThreadErrorState,
    sessionIdState,
    sessionState,
    sideViewState,
    tasklistState,
    threadIdToResumeState,
    tokenCountState,
    transcriptionTextState,
    wavRecorderState,
    wavStreamPlayerState,
} from "src/state";
import {
    IAction,
    ICommand,
    IElement,
    IMessageElement,
    IStep,
    ITasklistElement,
    IThread,
} from "src/types";
import {
    addMessage,
    deleteMessageById,
    updateMessageById,
    updateMessageContentById,
} from "src/utils/message";

import { OutputAudioChunk } from "./types/audio";

import { ChainlitContext } from "./context";
import type { IToken } from "./useChatData";

const useChatSession = () => {
    const client = useContext(ChainlitContext);
    const sessionId = useRecoilValue(sessionIdState);

    const [session, setSession] = useRecoilState(sessionState);
    const setIsAiSpeaking = useSetRecoilState(isAiSpeakingState);
    const [audioConnection, setAudioConnection] =
        useRecoilState(audioConnectionState);
    const setTranscriptionText = useSetRecoilState(transcriptionTextState);
    const resetChatSettingsValue = useResetRecoilState(chatSettingsValueState);
    const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);
    const setFirstUserInteraction = useSetRecoilState(firstUserInteraction);
    const setLoading = useSetRecoilState(loadingState);
    const setMcps = useSetRecoilState(mcpState);
    const wavStreamPlayer = useRecoilValue(wavStreamPlayerState);
    const wavRecorder = useRecoilValue(wavRecorderState);
    const setMessages = useSetRecoilState(messagesState);
    const setAskUser = useSetRecoilState(askUserState);
    const setCallFn = useSetRecoilState(callFnState);
    const setCommands = useSetRecoilState(commandsState);
    const setSideView = useSetRecoilState(sideViewState);
    const setElements = useSetRecoilState(elementState);
    const setTasklists = useSetRecoilState(tasklistState);
    const setActions = useSetRecoilState(actionState);
    const setChatSettingsInputs = useSetRecoilState(chatSettingsInputsState);
    const setTokenCount = useSetRecoilState(tokenCountState);
    const [chatProfile, setChatProfile] = useRecoilState(chatProfileState);
    const idToResume = useRecoilValue(threadIdToResumeState);
    const setThreadResumeError = useSetRecoilState(resumeThreadErrorState);

    const [currentThreadId, setCurrentThreadId] =
        useRecoilState(currentThreadIdState);

    // Use currentThreadId as thread id in websocket header
    useEffect(() => {
        if (session?.socket) {
            session.socket.auth["threadId"] = currentThreadId || "";
        }
    }, [currentThreadId]);

    // A backgrounded tab's throttled JS timers can let the socket sit
    // disconnected for a while unnoticed — socket.io's own backoff schedule
    // (up to reconnectionDelayMax between tries) then takes a while to
    // reconnect after the fact. Force an immediate attempt the moment the
    // tab regains focus instead of waiting that schedule out, so switching
    // back feels instant rather than stuck until the next scheduled retry.
    useEffect(() => {
        const onVisibilityChange = () => {
            if (
                document.visibilityState === "visible" &&
                session?.socket &&
                !session.socket.connected
            ) {
                session.socket.connect();
            }
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        return () =>
            document.removeEventListener(
                "visibilitychange",
                onVisibilityChange
            );
    }, [session]);

    const _connect = useCallback(
        async ({
            transports,
            userEnv,
        }: {
            transports?: string[];
            userEnv: Record<string, string>;
        }) => {
            const { protocol, host, pathname } = new URL(client.httpEndpoint);
            const uri = `${protocol}//${host}`;
            const path =
                pathname && pathname !== "/"
                    ? `${pathname}/ws/socket.io`
                    : "/ws/socket.io";

            try {
                await client.stickyCookie(sessionId);
            } catch (err) {
                console.error(`Failed to set sticky session cookie: ${err}`);
            }

            const socket = io(uri, {
                path,
                withCredentials: true,
                transports: transports || ["websocket", "polling"], // Try WebSocket first
                reconnection: true, // Enable auto-reconnection
                // Browsers throttle JS timers in backgrounded tabs, so a
                // disconnect can burn through a small retry budget while the
                // tab is invisible — by the time the user switches back,
                // socket.io has already given up for good (reconnect_failed
                // below), and only a manual reload recovers. Keep retrying
                // indefinitely instead; the visibilitychange handler further
                // below still forces an immediate attempt on refocus rather
                // than waiting out this backoff schedule.
                reconnectionAttempts: Infinity,
                reconnectionDelay: 1000, // Retry connection quickly
                reconnectionDelayMax: 5000, // Max retry delay
                timeout: 20000, // 20s connection timeout
                auth: {
                    clientType: client.type,
                    sessionId,
                    threadId: idToResume || "",
                    userEnv: JSON.stringify(userEnv),
                    chatProfile: chatProfile
                        ? encodeURIComponent(chatProfile)
                        : "",
                },
            });

            console.log(
                "Socket.io connecting with threadId:",
                idToResume || "(empty)"
            );

            setSession((old) => {
                old?.socket?.removeAllListeners();
                old?.socket?.close();
                return {
                    socket,
                };
            });

            socket.on("connect", () => {
                console.log(
                    "Socket.io connected via",
                    socket.io.engine.transport.name
                );
                socket.emit("connection_successful");
                setSession((s) => ({ ...s!, error: false }));
                setMcps((prev) =>
                    prev.map((mcp) => {
                        let promise;
                        if (mcp.clientType === "sse") {
                            promise = client.connectSseMCP(
                                sessionId,
                                mcp.name,
                                mcp.url!
                            );
                        } else if (mcp.clientType === "streamable-http") {
                            promise = client.connectStreamableHttpMCP(
                                sessionId,
                                mcp.name,
                                mcp.url!
                            );
                        } else {
                            promise = client.connectStdioMCP(
                                sessionId,
                                mcp.name,
                                mcp.command!
                            );
                        }
                        promise
                            .then(async ({ success, mcp }) => {
                                setMcps((prev) =>
                                    prev.map((existingMcp) => {
                                        if (existingMcp.name === mcp.name) {
                                            return {
                                                ...existingMcp,
                                                status: success
                                                    ? "connected"
                                                    : "failed",
                                                tools: mcp
                                                    ? mcp.tools
                                                    : existingMcp.tools,
                                            };
                                        }
                                        return existingMcp;
                                    })
                                );
                            })
                            .catch(() => {
                                setMcps((prev) =>
                                    prev.map((existingMcp) => {
                                        if (existingMcp.name === mcp.name) {
                                            return {
                                                ...existingMcp,
                                                status: "failed",
                                            };
                                        }
                                        return existingMcp;
                                    })
                                );
                            });
                        return { ...mcp, status: "connecting" };
                    })
                );
            });

            // Track if we've already switched to polling fallback
            let hasTriedPollingFallback = false;

            socket.on("connect_error", (error) => {
                // Detect HTTP 401 from the server (expired/invalid session cookie)
                const httpStatus = (error as any)?.description?.status;
                if (httpStatus === 401) {
                    console.log(
                        "Socket.io received 401, redirecting to login..."
                    );
                    socket.disconnect();
                    const basePath = new URL(
                        client.httpEndpoint
                    ).pathname.replace(/\/$/, "");
                    window.location.href = basePath + "/login";
                    return;
                }

                // If WebSocket fails on first try, switch to polling for better firewall compatibility
                if (!hasTriedPollingFallback) {
                    hasTriedPollingFallback = true;
                    console.log(
                        "WebSocket connection failed, switching to polling fallback..."
                    );

                    // Update transport order for next connection attempt
                    socket.io.opts.transports = ["polling", "websocket"] as any;

                    return; // Skip error display, socket.io will retry with polling
                }

                // If we get here, all transports failed
                console.error("Socket.io connection error:", error.message);
            });

            socket.io.on("reconnect_failed", () => {
                setSession((s) => ({ ...s!, error: true }));
            });

            socket.on("task_start", () => {
                setLoading(true);
            });

            socket.on("task_end", () => {
                setLoading(false);
            });

            socket.on("reload", () => {
                socket.emit("clear_session");
                window.location.reload();
            });

            socket.on("session_expired", () => {
                console.log("Session expired, redirecting to login...");
                socket.disconnect();
                const basePath = new URL(client.httpEndpoint).pathname.replace(
                    /\/$/,
                    ""
                );
                window.location.href = basePath + "/login";
            });

            // Track if we've sent audio_end to prevent duplicates
            let audioEndSent = false;

            // Track pending transcription messages that should stay in streaming state
            const pendingTranscriptionMessages = new Set<string>();

            socket.on("audio_connection", async (state: "on" | "off") => {
                console.log(
                    "Received audio_connection event with state:",
                    state
                );
                if (state === "on") {
                    // Don't start recording if we're already recording (prevents duplicate sessions)
                    if (audioConnection === "on") {
                        console.log(
                            "Already recording, ignoring duplicate audio_connection on event"
                        );
                        return;
                    }

                    // Reset audio_end tracking for new session
                    audioEndSent = false;

                    let isFirstChunk = true;
                    const startTime = Date.now();
                    const mimeType = "pcm16";

                    // Check if we should use test audio file or real microphone
                    const useTestAudio =
                        import.meta.env.VITE_DEV_STT_USE_TEST_AUDIO === "true";

                    // Define audio source interface - this is the ONLY difference between test and production
                    interface AudioSource {
                        start: () => Promise<void>;
                        stop: () => Promise<void>;
                    }

                    let audioSource: AudioSource;

                    if (useTestAudio) {
                        // TEST MODE: Load audio from file and simulate streaming
                        console.log("Using test audio file mode...");

                        audioSource = {
                            start: async () => {
                                try {
                                    // Fetch and decode the audio file
                                    const response = await fetch(
                                        "/speech-text.mp3"
                                    );
                                    if (!response.ok) {
                                        throw new Error(
                                            `Failed to fetch audio file: ${response.status} ${response.statusText}`
                                        );
                                    }
                                    const audioBlob = await response.blob();
                                    const audioContext = new AudioContext({
                                        sampleRate: 24000,
                                    });
                                    const arrayBuffer =
                                        await audioBlob.arrayBuffer();
                                    const audioBuffer =
                                        await audioContext.decodeAudioData(
                                            arrayBuffer
                                        );
                                    const audioData =
                                        audioBuffer.getChannelData(0);

                                    // Convert Float32 to Int16 (PCM16)
                                    const pcm16Data = new Int16Array(
                                        audioData.length
                                    );
                                    for (let i = 0; i < audioData.length; i++) {
                                        const s = Math.max(
                                            -1,
                                            Math.min(1, audioData[i])
                                        );
                                        pcm16Data[i] =
                                            s < 0 ? s * 0x8000 : s * 0x7fff;
                                    }

                                    console.log(
                                        `Loaded test audio: ${pcm16Data.length} samples`
                                    );

                                    // Send the audio in chunks to simulate real-time streaming
                                    const chunkSize = 4800; // ~200ms chunks at 24kHz
                                    for (
                                        let i = 0;
                                        i < pcm16Data.length;
                                        i += chunkSize
                                    ) {
                                        const chunk = pcm16Data.slice(
                                            i,
                                            i + chunkSize
                                        );
                                        const elapsedTime =
                                            Date.now() - startTime;

                                        socket.emit("audio_chunk", {
                                            isStart: isFirstChunk,
                                            mimeType,
                                            elapsedTime,
                                            data: chunk,
                                        });

                                        isFirstChunk = false;

                                        // Wait between chunks to simulate real-time
                                        await new Promise((resolve) =>
                                            setTimeout(resolve, 200)
                                        );
                                    }

                                    console.log(
                                        "Finished streaming test audio"
                                    );
                                } catch (error) {
                                    console.error(
                                        "Error loading test audio file:",
                                        error
                                    );
                                    throw error;
                                }
                            },
                            stop: async () => {
                                // Test audio stops automatically when all chunks are sent
                                console.log("Test audio source stopped");
                            },
                        };
                    } else {
                        // PRODUCTION MODE: Use real microphone
                        console.log("Using real microphone mode...");

                        audioSource = {
                            start: async () => {
                                try {
                                    await wavRecorder.begin();
                                    await wavRecorder.record(
                                        (data: { mono: Int16Array }) => {
                                            const elapsedTime =
                                                Date.now() - startTime;
                                            socket.emit("audio_chunk", {
                                                isStart: isFirstChunk,
                                                mimeType,
                                                elapsedTime,
                                                data: data.mono,
                                            });
                                            isFirstChunk = false;
                                        }
                                    );
                                } catch (error) {
                                    console.error(
                                        "Failed to start microphone:",
                                        error
                                    );
                                    // Emit audio_connection off to reset UI state
                                    socket.emit("audio_end");
                                    setAudioConnection("off");

                                    // Show user-friendly error message
                                    const errorMessage =
                                        error instanceof Error &&
                                        error.message.includes("media stream")
                                            ? "Microphone access denied. Please allow microphone access in your browser settings."
                                            : "Failed to access microphone. Please check your browser permissions.";

                                    toast.error(errorMessage);
                                    throw error;
                                }
                            },
                            stop: async () => {
                                if (wavRecorder.processor) {
                                    await wavRecorder.end();
                                }
                            },
                        };
                    }

                    // Common recording flow for both test and production
                    try {
                        await audioSource.start();

                        // For test audio, emit audio_end after chunks complete (if not already sent)
                        if (useTestAudio && !audioEndSent) {
                            console.log(
                                "Test audio completed, emitting audio_end..."
                            );
                            socket.emit("audio_end");
                            audioEndSent = true;
                        }

                        // Connect player for output
                        await wavStreamPlayer.connect();
                        wavStreamPlayer.onStop = () => setIsAiSpeaking(false);
                    } catch (error) {
                        // Error already handled in audioSource.start(), just prevent further execution
                        console.error("Audio source failed to start:", error);
                        return;
                    }
                } else {
                    // Common stop logic for both test and production
                    console.log("Stopping audio recording...");

                    // Stop the audio source (microphone only - test audio already stopped)
                    if (wavRecorder.processor) {
                        await wavRecorder.end();
                    }

                    // Emit audio_end for real microphone (test audio already did this)
                    const useTestAudio =
                        import.meta.env.VITE_DEV_STT_USE_TEST_AUDIO === "true";
                    if (!useTestAudio && !audioEndSent) {
                        console.log(
                            "Emitting audio_end to trigger transcription..."
                        );
                        socket.emit("audio_end");
                        audioEndSent = true;
                    } else if (useTestAudio) {
                        console.log(
                            "Test audio mode: audio_end already sent, just updating UI state"
                        );
                    }

                    await wavStreamPlayer.interrupt();
                }
                setAudioConnection(state);
            });

            socket.on("audio_chunk", (chunk: OutputAudioChunk) => {
                wavStreamPlayer.add16BitPCM(chunk.data, chunk.track);
                setIsAiSpeaking(true);
            });

            socket.on("audio_interrupt", () => {
                wavStreamPlayer.interrupt();
            });

            // Listen for streaming transcription results from backend
            let transcriptionMessageId: string | null = null;
            let lastPartialText = ""; // Track last partial to detect segment resets
            let completedSegments: string[] = []; // Track completed segments

            socket.on("transcription_partial", (data: { text: string }) => {
                console.log("Received partial transcription:", data.text);

                // Check if this is a new segment (text got shorter or doesn't start with previous)
                const isNewSegment =
                    data.text.length < lastPartialText.length &&
                    !lastPartialText.startsWith(data.text.substring(0, 20));

                if (isNewSegment && lastPartialText) {
                    // Save the last partial as a completed segment
                    completedSegments.push(lastPartialText);
                }

                lastPartialText = data.text;

                // Combine completed segments with current partial
                const fullText =
                    completedSegments.length > 0
                        ? completedSegments.join(" ") + " " + data.text
                        : data.text;

                if (!transcriptionMessageId) {
                    // Create a new temporary message for the transcription with a proper UUID
                    const messageId = uuidv4();
                    const tempMessage: IStep = {
                        id: messageId,
                        name: "Transcribing...",
                        type: "user_message",
                        output: fullText,
                        createdAt: new Date().toISOString(),
                        streaming: true,
                    };
                    transcriptionMessageId = messageId;
                    setMessages((oldMessages) =>
                        addMessage(oldMessages, tempMessage)
                    );
                } else {
                    // Update the existing transcription message (isSequence=true to replace, isInput=false for output)
                    setMessages((oldMessages) =>
                        updateMessageContentById(
                            oldMessages,
                            transcriptionMessageId!,
                            fullText,
                            true,
                            false
                        )
                    );
                }
            });

            socket.on("transcription_complete", (data: { text: string }) => {
                console.log("Transcription complete:", data.text);

                if (transcriptionMessageId && data.text && data.text.trim()) {
                    // Update the message content and author, but keep it in a processing state
                    // The backend will send back the proper message state after processing
                    setMessages((oldMessages) => {
                        const updated = updateMessageContentById(
                            oldMessages,
                            transcriptionMessageId!,
                            data.text,
                            true,
                            false
                        );
                        // Keep streaming flag to prevent showing feedback icons prematurely
                        // Just update the name from "Transcribing..." to "User"
                        return updateMessageById(
                            updated,
                            transcriptionMessageId!,
                            {
                                ...updated.find(
                                    (m) => m.id === transcriptionMessageId
                                )!,
                                streaming: true, // Keep streaming until backend confirms
                                name: "User",
                            }
                        );
                    });

                    // Send the transcribed text as a user message to the backend using the SAME ID
                    console.log(
                        "Sending transcribed message to agent:",
                        data.text
                    );

                    // Add to pending set to protect against any updates
                    pendingTranscriptionMessages.add(transcriptionMessageId);

                    socket.emit("client_message", {
                        message: {
                            id: transcriptionMessageId, // Use the same ID that's already in the UI
                            threadId: currentThreadId,
                            name: "User", // Use same name as displayed in UI
                            output: data.text,
                            type: "user_message",
                            createdAt: new Date().toISOString(),
                        },
                    });

                    // DON'T reset transcriptionMessageId here - keep it so stream_start can finalize it
                    // It will be reset when stream_start fires
                    lastPartialText = "";
                    completedSegments = [];
                } else if (transcriptionMessageId) {
                    // Empty or no transcription - remove the temporary message
                    setMessages((oldMessages) =>
                        deleteMessageById(oldMessages, transcriptionMessageId!)
                    );

                    // Reset state
                    transcriptionMessageId = null;
                    lastPartialText = "";
                    completedSegments = [];
                }
            });

            socket.on("resume_thread", (thread: IThread) => {
                const isReadOnlyView = Boolean(
                    (thread as any)?.metadata?.viewer_read_only
                );

                // Always set the thread ID from what the server sent
                if (!isReadOnlyView) {
                    setCurrentThreadId(thread.id);
                }

                // Log a warning if there's a mismatch, but don't redirect
                // The URL is the source of truth for which thread should be displayed
                if (!isReadOnlyView && idToResume && thread.id !== idToResume) {
                    console.warn(
                        `Thread ID mismatch: requested ${idToResume}, server returned ${thread.id}`
                    );
                }

                // Sort steps to ensure parents come before children
                // This fixes the issue where child steps (like on_message runs)
                // are processed before their parent steps (like user_message)
                const sortedSteps = [...thread.steps].sort((a, b) => {
                    // Steps without parentId (top-level) come first
                    if (!a.parentId && b.parentId) return -1;
                    if (a.parentId && !b.parentId) return 1;

                    // Then sort by createdAt to maintain chronological order
                    const dateA = new Date(a.createdAt || 0).getTime();
                    const dateB = new Date(b.createdAt || 0).getTime();
                    return dateA - dateB;
                });

                let messages: IStep[] = [];
                for (const step of sortedSteps) {
                    messages = addMessage(messages, step);
                }

                if (thread.metadata?.chat_profile) {
                    setChatProfile(thread.metadata?.chat_profile);
                }
                if (thread.metadata?.chat_settings) {
                    setChatSettingsValue(thread.metadata?.chat_settings);
                }
                setMessages(messages);

                const elements = thread.elements || [];
                setTasklists(
                    (elements as ITasklistElement[]).filter(
                        (e) => e.type === "tasklist"
                    )
                );
                setElements(
                    (elements as IMessageElement[]).filter(
                        (e) => ["avatar", "tasklist"].indexOf(e.type) === -1
                    )
                );

                // Restore sidebar elements: charts (display="side") and file-type elements
                const fileTypes = ["pdf", "file", "image", "video", "audio"];
                const sidebarElements = (elements as IMessageElement[]).filter(
                    (e) => e.display === "side" || fileTypes.includes(e.type)
                );
                if (sidebarElements.length > 0) {
                    setSideView({
                        title: "Charts",
                        elements: sidebarElements,
                        key: "Charts",
                    });
                }

                // Dispatch podcast status (sent from backend with thread)
                // Always dispatch to signal loading is complete, even if status is null
                const podcastStatus = (thread as any)?.podcast_status;
                const event = new CustomEvent("chainlit-podcast-status", {
                    detail: podcastStatus || { has_podcast: false },
                });
                window.dispatchEvent(event);
            });

            socket.on("resume_thread_error", (error?: string) => {
                setThreadResumeError(error);
            });

            socket.on("new_message", (message: IStep) => {
                setMessages((oldMessages) => addMessage(oldMessages, message));
            });

            socket.on(
                "first_interaction",
                (event: { interaction: string; thread_id: string }) => {
                    setFirstUserInteraction(event.interaction);
                    setCurrentThreadId(event.thread_id);
                }
            );

            socket.on("update_message", (message: IStep) => {
                // If this is updating a pending transcription message, preserve the streaming state
                // until stream_start fires
                if (pendingTranscriptionMessages.has(message.id)) {
                    console.log(
                        "update_message: Preserving streaming state for pending transcription message",
                        message.id
                    );
                    setMessages((oldMessages) =>
                        updateMessageById(oldMessages, message.id, {
                            ...message,
                            streaming: true,
                        })
                    );
                } else {
                    setMessages((oldMessages) =>
                        updateMessageById(oldMessages, message.id, message)
                    );
                }
            });

            socket.on("delete_message", (message: IStep) => {
                setMessages((oldMessages) =>
                    deleteMessageById(oldMessages, message.id)
                );
            });

            socket.on("stream_start", (message: IStep) => {
                // When agent starts responding, finalize any pending transcription messages
                if (transcriptionMessageId) {
                    console.log(
                        "stream_start: Finalizing transcription message",
                        transcriptionMessageId
                    );
                    setMessages((oldMessages) => {
                        const msg = oldMessages.find(
                            (m) => m.id === transcriptionMessageId
                        );
                        if (msg) {
                            return updateMessageById(
                                oldMessages,
                                transcriptionMessageId!,
                                {
                                    ...msg,
                                    streaming: false,
                                }
                            );
                        }
                        console.warn(
                            "stream_start: Transcription message not found",
                            transcriptionMessageId
                        );
                        return oldMessages;
                    });
                    // Remove from pending set and reset ID
                    pendingTranscriptionMessages.delete(transcriptionMessageId);
                    transcriptionMessageId = null;
                }

                setMessages((oldMessages) => addMessage(oldMessages, message));
            });

            socket.on(
                "stream_token",
                ({ id, token, isSequence, isInput }: IToken) => {
                    setMessages((oldMessages) =>
                        updateMessageContentById(
                            oldMessages,
                            id,
                            token,
                            isSequence,
                            isInput
                        )
                    );
                }
            );

            socket.on("ask", ({ msg, spec }, callback) => {
                setAskUser({ spec, callback, parentId: msg.parentId });
                setMessages((oldMessages) => addMessage(oldMessages, msg));

                setLoading(false);
            });

            socket.on("ask_timeout", () => {
                setAskUser(undefined);
                setLoading(false);
            });

            socket.on("clear_ask", () => {
                setAskUser(undefined);
            });

            socket.on("call_fn", ({ name, args }, callback) => {
                setCallFn({ name, args, callback });
            });

            socket.on("clear_call_fn", () => {
                setCallFn(undefined);
            });

            socket.on("call_fn_timeout", () => {
                setCallFn(undefined);
            });

            socket.on("chat_settings", (inputs: any) => {
                setChatSettingsInputs(inputs);
                resetChatSettingsValue();
            });

            socket.on("set_commands", (commands: ICommand[]) => {
                setCommands(commands);
            });

            socket.on("set_sidebar_title", (title: string) => {
                setSideView((prev) => {
                    if (prev?.title === title) return prev;
                    return { title, elements: prev?.elements || [] };
                });
            });

            socket.on(
                "set_sidebar_elements",
                ({
                    elements,
                    key,
                }: {
                    elements: IMessageElement[];
                    key?: string;
                }) => {
                    // Define element types once for reuse
                    const chartTypes = ["custom", "plotly", "tasklist"];
                    const fileTypes = [
                        "pdf",
                        "file",
                        "image",
                        "video",
                        "audio",
                    ];

                    if (!elements.length) {
                        // If empty elements with key="Charts", only remove chart-type elements, keep file elements
                        if (key === "Charts") {
                            setSideView((prev) => {
                                if (!prev?.elements) {
                                    return prev; // No elements to filter
                                }

                                // Keep only file elements, remove chart elements
                                const remainingElements = prev.elements.filter(
                                    (el) => fileTypes.includes(el.type)
                                );

                                // If no elements left, clear sidebar completely
                                if (remainingElements.length === 0) {
                                    return undefined;
                                }

                                // Otherwise update with remaining file elements
                                return { ...prev, elements: remainingElements };
                            });
                        } else {
                            // For other keys or no key, clear sidebar completely as before
                            setSideView(undefined);
                        }
                    } else {
                        elements.forEach((element) => {
                            if (!element.url && element.chainlitKey) {
                                element.url = client.getElementUrl(
                                    element.chainlitKey,
                                    sessionId
                                );
                            }
                        });
                        setSideView((prev) => {
                            // Check if both key and elements are the same to avoid unnecessary updates
                            if (
                                prev?.key === key &&
                                prev?.elements?.length === elements.length
                            ) {
                                // Compare element IDs to see if they're actually the same
                                const prevIds = prev.elements
                                    .map((el) => el.id)
                                    .sort();
                                const newIds = elements
                                    .map((el) => el.id)
                                    .sort();
                                if (
                                    prevIds.length === newIds.length &&
                                    prevIds.every(
                                        (id, index) => id === newIds[index]
                                    )
                                ) {
                                    return prev;
                                }
                            }

                            // If key is "Charts", preserve file elements and only replace chart elements
                            if (key === "Charts") {
                                const existingElements = prev?.elements || [];

                                // Keep existing file elements, replace all chart elements with new ones
                                const preservedFileElements =
                                    existingElements.filter((el) =>
                                        fileTypes.includes(el.type)
                                    );
                                const mergedElements = [
                                    ...preservedFileElements,
                                    ...elements,
                                ];

                                return {
                                    title: prev?.title || "",
                                    elements: mergedElements,
                                    key,
                                };
                            }

                            // For other keys, merge new elements with existing elements instead of replacing
                            // Keep existing elements that aren't in the new elements list
                            const existingElements = prev?.elements || [];
                            const newElementIds = new Set(
                                elements.map((el) => el.id)
                            );
                            const preservedElements = existingElements.filter(
                                (el) => !newElementIds.has(el.id)
                            );
                            const mergedElements = [
                                ...preservedElements,
                                ...elements,
                            ];

                            return {
                                title: prev?.title || "",
                                elements: mergedElements,
                                key,
                            };
                        });
                    }
                }
            );

            socket.on("element", (element: IElement) => {
                if (!element.url && element.chainlitKey) {
                    element.url = client.getElementUrl(
                        element.chainlitKey,
                        sessionId
                    );
                }

                if (element.type === "tasklist") {
                    setTasklists((old) => {
                        const index = old.findIndex((e) => e.id === element.id);
                        if (index === -1) {
                            return [...old, element];
                        } else {
                            return [
                                ...old.slice(0, index),
                                element,
                                ...old.slice(index + 1),
                            ];
                        }
                    });
                } else {
                    setElements((old) => {
                        const index = old.findIndex((e) => e.id === element.id);
                        if (index === -1) {
                            return [...old, element];
                        } else {
                            return [
                                ...old.slice(0, index),
                                element,
                                ...old.slice(index + 1),
                            ];
                        }
                    });

                    // Add element to sidebar if it has display="side" OR is a file type
                    // File-type elements use display="inline" for the thread view but
                    // must also appear in the sidebar uploads panel.
                    const fileTypes = [
                        "pdf",
                        "file",
                        "image",
                        "video",
                        "audio",
                    ];
                    const isFileType = fileTypes.includes(element.type);
                    if (
                        (element as IMessageElement).display === "side" ||
                        isFileType
                    ) {
                        setSideView((prev) => {
                            const existingElements = prev?.elements || [];
                            const index = existingElements.findIndex(
                                (e) => e.id === element.id
                            );

                            let updatedElements;
                            if (index === -1) {
                                // Add new element
                                updatedElements = [
                                    ...existingElements,
                                    element as IMessageElement,
                                ];
                            } else {
                                // Update existing element
                                updatedElements = [
                                    ...existingElements.slice(0, index),
                                    element as IMessageElement,
                                    ...existingElements.slice(index + 1),
                                ];
                            }

                            return {
                                title: prev?.title || "Charts",
                                elements: updatedElements,
                                key: prev?.key || "Charts",
                            };
                        });
                    }
                }
            });

            socket.on("remove_element", (remove: { id: string }) => {
                setElements((old) => {
                    return old.filter((e) => e.id !== remove.id);
                });
                setTasklists((old) => {
                    return old.filter((e) => e.id !== remove.id);
                });
            });

            socket.on("action", (action: IAction) => {
                setActions((old) => {
                    if (
                        old.some(
                            (a) =>
                                a.name === action.name &&
                                a.forId === action.forId
                        )
                    ) {
                        return old;
                    }
                    return [...old, action];
                });
            });

            socket.on("remove_action", (action: IAction) => {
                setActions((old) => {
                    const index = old.findIndex((a) => a.id === action.id);
                    if (index === -1) return old;
                    return [...old.slice(0, index), ...old.slice(index + 1)];
                });
            });

            socket.on("token_usage", (count: number) => {
                setTokenCount((old) => old + count);
            });

            socket.on("window_message", (data: any) => {
                if (window.parent) {
                    window.parent.postMessage(data, "*");
                }
            });

            socket.on("toast", (data: { message: string; type: string }) => {
                if (!data.message) {
                    console.warn("No message received for toast.");
                    return;
                }

                switch (data.type) {
                    case "info":
                        toast.info(data.message);
                        break;
                    case "error":
                        toast.error(data.message);
                        break;
                    case "success":
                        toast.success(data.message);
                        break;
                    case "warning":
                        toast.warning(data.message);
                        break;
                    default:
                        toast(data.message);
                        break;
                }
            });
        },
        [setSession, sessionId, idToResume, chatProfile]
    );

    const connect = useCallback(debounce(_connect, 200), [_connect]);

    const disconnect = useCallback(() => {
        if (session?.socket) {
            session.socket.removeAllListeners();
            session.socket.close();
        }
    }, [session]);

    return {
        connect,
        disconnect,
        session,
        sessionId,
        chatProfile,
        idToResume,
        setChatProfile,
    };
};

export { useChatSession };
